import { useCallback, useState } from "react";
import type { SyntheticEvent } from "react";

import { useTranslation } from "react-i18next";

import { useMutation } from "@tanstack/react-query";
import {
  AsteriskIcon,
  CheckIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { countries, regions } from "simplydo/core/countries";
import { asMutation } from "simplydo/core/query";
import { css, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import type { DropdownProps } from "simplydo/react/ui/dropdown";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import type { InputProps } from "simplydo/react/ui/input";
import { Input } from "simplydo/react/ui/input";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import type {
  EditableAddress,
  EditableContact,
  IdeaBusinessProfile,
  IdeaBusinessProfileAddress,
  IdeaBusinessProfileContact,
  IdeaBusinessProfileUpdate,
  StoredIdeaBusinessProfile,
} from "../types";
import { toEditableAddress, toEditableContact } from "../types";

type ContactProps = {
  businessProfile: IdeaBusinessProfile;
  onSave?: (businessProfile: StoredIdeaBusinessProfile) => void;
};

export const ContactLayout = ({
  contact,
}: {
  contact: IdeaBusinessProfileContact;
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div>
        <b>{contact.fullName}</b>
        <p>{contact.jobTitle}</p>
      </div>
      <Divider fitted />
      <div>
        <Icon icon={MailIcon} style={{ marginRight: 3 }} />
        {contact.email || <span style={{ color: "#9a9a9a" }}>No email</span>}
      </div>
      <div>
        <Icon icon={PhoneIcon} style={{ marginRight: 3 }} />
        {contact.phoneNumber || (
          <span style={{ color: "#9a9a9a" }}>No phone number</span>
        )}
      </div>
    </div>
  );
};

export const AddressLayout = ({
  address,
}: {
  address?: IdeaBusinessProfileAddress;
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <p style={{ marginBottom: 0 }}>
        <Icon icon={MapPinIcon} style={{ marginRight: 3 }} />
        {address?.streetAddress || (
          <span style={{ color: "#9a9a9a" }}>No street address</span>
        )}
      </p>
      <div style={{ marginLeft: 20 }}>
        {address?.country ? (
          <p style={{ marginBottom: 4 }}>
            {countries.find((c) => c.iso2 === address.country)?.name}{" "}
            {address.region ? (
              <span>
                - {regions.find((r) => r.value === address.region)?.name}
              </span>
            ) : (
              ""
            )}
          </p>
        ) : (
          <span style={{ color: "#9a9a9a" }}>No country</span>
        )}
        <p style={{ marginBottom: 4 }}>
          {address?.postcode || (
            <span style={{ color: "#9a9a9a" }}>No postcode</span>
          )}
        </p>
        <p style={{ marginBottom: 4 }}>
          {address?.city || <span style={{ color: "#9a9a9a" }}>No city</span>}
        </p>
      </div>
    </div>
  );
};

const minInputClass = css`
  min-width: 0;
`;

const Contact = ({ businessProfile, onSave }: ContactProps) => {
  const [editingContact, setEditingContact] = useState<EditableContact | null>(
    null,
  );
  const [isNewContact, setIsNewContact] = useState(false);
  const [editingAddress, setEditingAddress] = useState<EditableAddress | null>(
    null,
  );
  const [isNewAddress, setIsNewAddress] = useState(false);
  const { address, contacts, addresses } = businessProfile || {};

  const user = useCurrentUser();
  const { t } = useTranslation();

  const canEditProfile = util.hasPermission(
    user,
    "ideaBusinessProfile.editDetails",
    businessProfile._id,
  );

  const updateProfileMutation = useMutation({
    mutationKey: ["post", "users", "profiles", businessProfile._id],
    mutationFn: asMutation(api.users.updateProfile)!,
    // Each save sends only the field it changed, so they must all be sent,
    // and in order.
    scope: { id: `businessProfile-${businessProfile._id}` },
    onSuccess: (data) => {
      onSave?.(data.ideaBusinessProfile);
    },
  });

  const updateProfile = useCallback(
    (data: IdeaBusinessProfileUpdate) => {
      api.setQueryData(
        api.users.getProfile,
        [businessProfile._id],
        (prev) =>
          ({
            ideaBusinessProfile: { ...prev.ideaBusinessProfile, ...data },
          }) as typeof prev,
      );

      updateProfileMutation.mutate([businessProfile._id, data]);
    },
    [updateProfileMutation, businessProfile._id],
  );

  return (
    <Grid mobile={1} columns={2}>
      <GridColumn>
        <H4>
          Contacts
          {canEditProfile ? (
            <Icon
              icon={AsteriskIcon}
              style={{ position: "relative", top: -5, marginLeft: 3 }}
              color="red"
              size="small"
            />
          ) : null}
          <Subheader>These people are affiliated with your business.</Subheader>
        </H4>
        <div>
          {contacts?.map((contact) => (
            <Card elevation="small" fluid key={contact.id} placeholder>
              <CardContent>
                {editingContact?.id === contact.id ? (
                  <>
                    <H4>Updating contact details</H4>
                    <FormField required>
                      <FormLabel>Full name</FormLabel>
                      <Input
                        fluid
                        placeholder={t("placeholder.fullName")}
                        defaultValue={contact.fullName}
                        onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                          setEditingContact((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            return { ...prev, fullName: String(value ?? "") };
                          })
                        }
                      />
                    </FormField>
                    <FormField required>
                      <FormLabel>Position or job title</FormLabel>
                      <Input
                        fluid
                        placeholder="Engineer"
                        defaultValue={contact.jobTitle}
                        onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                          setEditingContact((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            return { ...prev, jobTitle: String(value ?? "") };
                          })
                        }
                      />
                    </FormField>
                    <Grid columns={2} mobile={1}>
                      <GridColumn>
                        <FormField required>
                          <FormLabel>Phone number</FormLabel>
                          <Input
                            fluid
                            className={minInputClass}
                            placeholder="+44 0000 000000"
                            defaultValue={contact.phoneNumber}
                            onChange={(
                              _e: SyntheticEvent,
                              { value }: InputProps,
                            ) =>
                              setEditingContact((prev) => {
                                if (!prev) {
                                  return prev;
                                }
                                return {
                                  ...prev,
                                  phoneNumber: String(value ?? ""),
                                };
                              })
                            }
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField required>
                          <FormLabel>Contact email</FormLabel>
                          <Input
                            fluid
                            className={minInputClass}
                            placeholder={t("placeholder.email")}
                            defaultValue={contact.email}
                            onChange={(
                              _e: SyntheticEvent,
                              { value }: InputProps,
                            ) =>
                              setEditingContact((prev) => {
                                if (!prev) {
                                  return prev;
                                }
                                return { ...prev, email: String(value ?? "") };
                              })
                            }
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <div style={{ marginTop: "0.5rem" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          if (!editingContact) {
                            return;
                          }
                          updateProfile({
                            contacts: (contacts ?? []).map((c) =>
                              c.id === editingContact.id ? editingContact : c,
                            ),
                          });
                          setEditingContact(null);
                          setIsNewContact(false);
                        }}
                        color="secondary"
                      >
                        <Icon icon={CheckIcon} />
                        Save contact
                      </Button>
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => {
                          if (!editingContact) {
                            return;
                          }
                          if (isNewContact) {
                            updateProfile({
                              contacts: (contacts ?? []).filter(
                                (c) => c.id !== editingContact.id,
                              ),
                            });
                          }
                          setEditingContact(null);
                          setIsNewContact(false);
                        }}
                      >
                        <Icon icon={XIcon} />
                        {t("generic.cancel")}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <ContactLayout contact={contact} />
                    {canEditProfile ? (
                      <div style={{ justifySelf: "flex-end", marginTop: 6 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setIsNewContact(false);
                            setEditingContact(toEditableContact(contact));
                          }}
                        >
                          <Icon icon={PencilIcon} />
                          Edit
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            util
                              .confirm(
                                "Are you sure you want to delete this contact?",
                                "This action cannot be undone.",
                              )
                              .then(() => {
                                updateProfile({
                                  contacts: (contacts ?? []).filter(
                                    (c) => c.id !== contact.id,
                                  ),
                                });
                              })
                              .catch(() => {});
                          }}
                          variant="outline"
                        >
                          <Icon icon={Trash2Icon} />
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
          {editingContact === null && canEditProfile ? (
            <>
              {!contacts?.length ? (
                <Message color="blue">
                  <MessageHeader>No contacts</MessageHeader>
                  <p>
                    Please add at least one person as a primary contact for your
                    business.
                  </p>
                </Message>
              ) : null}
              <Button
                onClick={() => {
                  const contact = {
                    id: uuidv4(),
                    fullName: "",
                    email: "",
                    phoneNumber: "",
                    jobTitle: "",
                  };
                  if (!contacts?.length && user) {
                    contact.fullName = user.profile?.fullName ?? "";
                    contact.jobTitle = user.profile?.jobTitle ?? "";
                    contact.email = user.emails[0]?.address ?? "";
                    contact.phoneNumber = user.phoneNumbers?.[0]?.number ?? "";
                  }
                  updateProfile({ contacts: [...(contacts || []), contact] });
                  setIsNewContact(true);
                  setEditingContact(contact);
                }}
                color="secondary"
              >
                <Icon icon={PlusIcon} />
                Add contact
              </Button>
            </>
          ) : null}
        </div>
      </GridColumn>
      <GridColumn>
        <H4>
          Addresses
          {canEditProfile ? (
            <Icon
              icon={AsteriskIcon}
              style={{ position: "relative", top: -5, marginLeft: 3 }}
              color="red"
              size="small"
            />
          ) : null}
          <Subheader>
            These are the addresses associated with your business.
          </Subheader>
        </H4>
        <div>
          <Card fluid elevation="small" placeholder>
            <CardContent>
              <H5>
                Registered address
                <Subheader>
                  This address can be edited from the Profile tab
                </Subheader>
              </H5>
              <Divider />
              <AddressLayout address={address} />
            </CardContent>
          </Card>
          {addresses?.map((address) => (
            <Card fluid key={address.id} elevation="small" placeholder>
              <CardContent>
                {editingAddress?.id === address.id ? (
                  <>
                    <H4>Updating address details</H4>
                    <FormField required>
                      <FormLabel>Street address</FormLabel>
                      <Input
                        fluid
                        defaultValue={address.streetAddress}
                        onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                          setEditingAddress((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            return {
                              ...prev,
                              streetAddress: String(value ?? ""),
                            };
                          })
                        }
                      />
                    </FormField>
                    <FormField required>
                      <FormLabel>Country</FormLabel>
                      <Dropdown
                        variant="outline"
                        search
                        clearable
                        value={editingAddress?.country}
                        onChange={(
                          _e: SyntheticEvent,
                          { value }: DropdownProps,
                        ) =>
                          setEditingAddress((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const country = String(value ?? "");
                            return {
                              ...prev,
                              country,
                              region:
                                country !== "GB" ? "" : (address?.region ?? ""),
                            };
                          })
                        }
                        placeholder="Select one"
                        options={countries.map((c) => ({
                          key: c.iso2,
                          value: c.iso2,
                          text: c.name,
                        }))}
                      />
                    </FormField>
                    {editingAddress?.country === "GB" ||
                    editingAddress?.country === "England" ||
                    editingAddress?.country === "United Kingdom" ? (
                      <FormField required>
                        <FormLabel>Region</FormLabel>
                        <Dropdown
                          variant="outline"
                          search
                          clearable
                          value={editingAddress?.region}
                          onChange={(
                            _e: SyntheticEvent,
                            { value }: DropdownProps,
                          ) =>
                            setEditingAddress((prev) => {
                              if (!prev) {
                                return prev;
                              }
                              return { ...prev, region: String(value ?? "") };
                            })
                          }
                          options={regions.map((r) => ({
                            key: r.value,
                            value: r.value,
                            text: r.name,
                          }))}
                        />
                      </FormField>
                    ) : null}
                    <Grid columns={2} mobile={1}>
                      <GridColumn>
                        <FormField required>
                          <FormLabel>City</FormLabel>
                          <Input
                            fluid
                            className={minInputClass}
                            defaultValue={address.city}
                            onChange={(
                              _e: SyntheticEvent,
                              { value }: InputProps,
                            ) =>
                              setEditingAddress((prev) => {
                                if (!prev) {
                                  return prev;
                                }
                                return { ...prev, city: String(value ?? "") };
                              })
                            }
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField required>
                          <FormLabel>Postcode</FormLabel>
                          <Input
                            fluid
                            className={minInputClass}
                            defaultValue={address.postcode}
                            onChange={(
                              _e: SyntheticEvent,
                              { value }: InputProps,
                            ) =>
                              setEditingAddress((prev) => {
                                if (!prev) {
                                  return prev;
                                }
                                return {
                                  ...prev,
                                  postcode: String(value ?? ""),
                                };
                              })
                            }
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <div style={{ marginTop: "0.5rem" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          if (!editingAddress) {
                            return;
                          }
                          updateProfile({
                            addresses: (addresses ?? []).map((a) =>
                              a.id === editingAddress.id ? editingAddress : a,
                            ),
                          });
                          setEditingAddress(null);
                          setIsNewAddress(false);
                        }}
                        color="secondary"
                      >
                        <Icon icon={CheckIcon} />
                        Save address
                      </Button>
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => {
                          if (!editingAddress) {
                            return;
                          }
                          if (isNewAddress) {
                            updateProfile({
                              addresses: (addresses ?? []).filter(
                                (a) => a.id !== editingAddress.id,
                              ),
                            });
                          }
                          setEditingAddress(null);
                          setIsNewAddress(false);
                        }}
                      >
                        <Icon icon={XIcon} />
                        {t("generic.cancel")}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <AddressLayout address={address} />
                    {canEditProfile ? (
                      <div style={{ justifySelf: "flex-end", marginTop: 6 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setIsNewAddress(false);
                            setEditingAddress(toEditableAddress(address));
                          }}
                        >
                          <Icon icon={PencilIcon} />
                          Edit
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            util
                              .confirm(
                                "Are you sure you want to delete this address?",
                                "This action cannot be undone.",
                              )
                              .then(() => {
                                updateProfile({
                                  addresses: (addresses ?? []).filter(
                                    (c) => c.id !== address.id,
                                  ),
                                });
                              })
                              .catch(() => {});
                          }}
                          variant="outline"
                        >
                          <Icon icon={Trash2Icon} />
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
          {editingAddress === null && canEditProfile ? (
            <>
              {!addresses?.length && !address ? (
                <Message color="blue">
                  <MessageHeader>No addresses</MessageHeader>
                  <p>
                    Please add at least one address as a registered address for
                    your business.
                  </p>
                </Message>
              ) : null}
              <Button
                onClick={() => {
                  const address = {
                    id: uuidv4(),
                    streetAddress: "",
                    city: "",
                    postcode: "",
                    country: "",
                    region: "",
                  };
                  updateProfile({ addresses: [...(addresses || []), address] });
                  setIsNewAddress(true);
                  setEditingAddress(address);
                }}
                color="secondary"
              >
                <Icon icon={PlusIcon} />
                Add address
              </Button>
            </>
          ) : null}
        </div>
      </GridColumn>
    </Grid>
  );
};

export default Contact;
